import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Stack, Button } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  RecurringInvoiceFragmentFragment,
  DeleteRecurringInvoiceDocument,
  DeleteRecurringInvoiceMutation,
  DeleteRecurringInvoiceMutationVariables,
  UndeleteRecurringInvoiceDocument,
  UndeleteRecurringInvoiceMutation,
  UndeleteRecurringInvoiceMutationVariables,
  SuspendRecurringInvoiceDocument,
  SuspendRecurringInvoiceMutation,
  SuspendRecurringInvoiceMutationVariables,
  UnsuspendRecurringInvoiceDocument,
  UnsuspendRecurringInvoiceMutation,
  UnsuspendRecurringInvoiceMutationVariables,
  CreateRecurringInvoiceMutation,
  CreateRecurringInvoiceMutationVariables,
  CreateRecurringInvoiceDocument,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';
import { useNavigate } from 'react-router';
import { appPaths } from '../../../app-paths';

interface RecurringInvoiceListProps {
  contractId: string;
  recurringInvoices: RecurringInvoiceFragmentFragment[];
  onRefresh: () => void;
  disabled?: boolean;
}

const frequencyLabels: Record<string, string> = {
  W: 'týždenne',
  W2: 'každé 2 týždne',
  M: 'každý mesiac',
  M2: 'každé dva mesiace',
  M3: 'každé 3 mesiace',
  M6: 'každých 6 mesiacov',
  Y: 'ročne',
};

export function RecurringInvoiceList({
  contractId,
  recurringInvoices,
  onRefresh,
  disabled,
}: RecurringInvoiceListProps) {
  const app = useApp();
  const { show } = useAlert();

  const navigate = useNavigate();

  const [createRecurringInvoice, { loading: createLoading }] = useMutation<
    CreateRecurringInvoiceMutation,
    CreateRecurringInvoiceMutationVariables
  >(CreateRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa vytvoriť pravidelnú faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Pravidelná faktúra bola vytvorená.');
      onRefresh();
    },
  });

  const [deleteRecurringInvoice] = useMutation<DeleteRecurringInvoiceMutation, DeleteRecurringInvoiceMutationVariables>(
    DeleteRecurringInvoiceDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa zmazať pravidelnú faktúru.', e.message);
      },
      onCompleted: () => {
        show.info('Pravidelná faktúra bola zmazaná.');
        onRefresh();
      },
    },
  );

  const [undeleteRecurringInvoice] = useMutation<
    UndeleteRecurringInvoiceMutation,
    UndeleteRecurringInvoiceMutationVariables
  >(UndeleteRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa obnoviť pravidelnú faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Pravidelná faktúra bola obnovená.');
      onRefresh();
    },
  });

  const [suspendRecurringInvoice] = useMutation<
    SuspendRecurringInvoiceMutation,
    SuspendRecurringInvoiceMutationVariables
  >(SuspendRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa pozastaviť pravidelnú faktúru.', e.message);
    },
  });

  const [unsuspendRecurringInvoice] = useMutation<
    UnsuspendRecurringInvoiceMutation,
    UnsuspendRecurringInvoiceMutationVariables
  >(UnsuspendRecurringInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa aktivovať pravidelnú faktúru.', e.message);
    },
  });

  const columns = React.useMemo((): GridColDef<RecurringInvoiceFragmentFragment>[] => {
    return [
      {
        field: 'nextDate',
        headerName: 'Ďalšia faktúra',
        width: 150,
        valueFormatter: (value: string) => app.formatDate(value),
      },
      {
        field: 'frequency',
        headerName: 'Frekvencia',
        width: 150,
        valueGetter: (value: string) => frequencyLabels[value] || value,
      },
      {
        field: 'stopAfterDate',
        headerName: 'Zastaviť po',
        width: 130,
        valueFormatter: (value: string | null) => (value ? app.formatDate(value) : ''),
      },
      {
        field: 'dueDays',
        headerName: 'Splatnosť (dní)',
        width: 120,
      },
      {
        field: 'totalAmount',
        headerName: 'Suma',
        width: 100,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'description',
        headerName: 'Popis',
        width: 250,
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Akcie',
        width: 120,
        getActions: (params) => {
          const invoice = params.row;
          const actions = [];

          if (invoice.deletedAt) {
            actions.push(
              <GridActionsCellItem
                key="undelete"
                icon={<RestoreIcon />}
                label="Obnoviť"
                onClick={() => undeleteRecurringInvoice({ variables: { recurringInvoiceId: invoice.id } })}
                showInMenu
              />,
            );
          } else {
            if (invoice.suspended) {
              actions.push(
                <GridActionsCellItem
                  key="unsuspend"
                  icon={<PlayArrowIcon />}
                  label="Aktivovať"
                  onClick={() => unsuspendRecurringInvoice({ variables: { recurringInvoiceId: invoice.id } })}
                  showInMenu
                />,
              );
            } else {
              actions.push(
                <GridActionsCellItem
                  key="suspend"
                  icon={<PauseIcon />}
                  label="Pozastaviť"
                  onClick={() => suspendRecurringInvoice({ variables: { recurringInvoiceId: invoice.id } })}
                  showInMenu
                />,
              );
            }

            actions.push(
              <GridActionsCellItem
                key="delete"
                icon={<DeleteIcon />}
                label="Zmazať"
                onClick={() => deleteRecurringInvoice({ variables: { recurringInvoiceId: invoice.id } })}
                showInMenu
              />,
            );
          }

          return actions;
        },
      },
    ];
  }, [app, deleteRecurringInvoice, undeleteRecurringInvoice, suspendRecurringInvoice, unsuspendRecurringInvoice]);

  const isInactive = (invoice: RecurringInvoiceFragmentFragment) => {
    if (invoice.deletedAt || invoice.suspended) return true;
    if (invoice.stopAfterDate && new Date(invoice.stopAfterDate) < new Date()) return true;
    return false;
  };

  const handleCreateClick = async () => {
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + 1);
    try {
      const result = await createRecurringInvoice({
        variables: {
          input: {
            contractId,
            nextDate: nextDate.toISOString(),
            frequency: 'M',
            stopAfterDate: undefined,
            dueDays: 14,
            description: ' ',
            totalAmount: 0,
          },
        },
      });
      if (result.data?.createRecurringInvoice.recurringInvoice?.id) {
        navigate(
          appPaths.recurringInvoice.navigate({
            recurringInvoiceId: result.data.createRecurringInvoice.recurringInvoice.id,
          }),
        );
      }
    } catch (error) {
      // Error handling is done in the mutation's onError
      show.error('Nepodarilo sa vytvoriť pravidelnú faktúru.');
    }
  };

  return (
    <Stack direction="column" spacing={2} sx={{ height: 450 }}>
      <Button
        variant="outlined"
        onClick={handleCreateClick}
        startIcon={<AddIcon />}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        Vytvor pravidelnú faktúru
      </Button>

      <DataGrid
        rows={recurringInvoices}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          sorting: {
            sortModel: [{ field: 'nextDate', sort: 'asc' }],
          },
        }}
        pageSizeOptions={[5, 10, 20]}
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(appPaths.recurringInvoice.navigate({ recurringInvoiceId: params.row.id }))}
        getRowClassName={(params) => (isInactive(params.row) ? 'inactive-row' : '')}
        sx={{
          '& .inactive-row': {
            color: 'text.disabled',
            backgroundColor: 'action.hover',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
    </Stack>
  );
}
